import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/api';
import { useState } from 'react';

type OverrideRow = {
  id: number;
  locale: string;
  message_key: string;
  value: string;
};

const LOCALES = ['pt-BR', 'es', 'en-US'] as const;

export function TranslationsPage() {
  const qc = useQueryClient();
  const [locale, setLocale] = useState<(typeof LOCALES)[number]>('pt-BR');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['ui-messages', locale],
    queryFn: () =>
      adminFetch<{ locale: string; overrides: OverrideRow[] }>(
        `/ui-messages?locale=${encodeURIComponent(locale)}`,
      ),
  });

  const save = useMutation({
    mutationFn: () =>
      adminFetch('/ui-messages', {
        method: 'PUT',
        body: JSON.stringify({ locale, message_key: key.trim(), value }),
      }),
    onSuccess: () => {
      setKey('');
      setValue('');
      void qc.invalidateQueries({ queryKey: ['ui-messages', locale] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminFetch(`/ui-messages/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ui-messages', locale] }),
  });

  const overrides = data?.overrides ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Traduções</h1>
        <p className="text-sm text-neutral-400">
          Overrides da interface (sobre o dicionário estático). Chaves novas nascem no código.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {LOCALES.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLocale(l)}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              locale === l ? 'bg-sky-700 text-white' : 'border border-neutral-700 text-neutral-300'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="grid gap-2 rounded-xl border border-neutral-800 p-4 sm:grid-cols-3">
        <input
          className="rounded border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm sm:col-span-1"
          placeholder="chave (ex: nav.animals)"
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <input
          className="rounded border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm sm:col-span-1"
          placeholder="texto"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          type="button"
          disabled={!key.trim() || !value.trim() || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-lg bg-sky-700 px-3 py-2 text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          Salvar override
        </button>
        {save.isError && (
          <p className="text-xs text-red-400 sm:col-span-3">
            {save.error instanceof Error ? save.error.message : 'Erro'}
          </p>
        )}
      </div>

      {isLoading && <p className="text-neutral-400">Carregando…</p>}
      {error && <p className="text-red-400">{error instanceof Error ? error.message : 'Erro'}</p>}

      <div className="overflow-hidden rounded-xl border border-neutral-800">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-neutral-900 text-neutral-400">
            <tr>
              <th className="px-4 py-3 font-medium">Chave</th>
              <th className="px-4 py-3 font-medium">Valor</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {overrides.map((row) => (
              <tr key={row.id} className="border-t border-neutral-800">
                <td className="px-4 py-2 font-mono text-xs text-sky-300">{row.message_key}</td>
                <td className="px-4 py-2">{row.value}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    className="text-xs text-red-400 hover:underline"
                    onClick={() => {
                      setKey(row.message_key);
                      setValue(row.value);
                    }}
                  >
                    editar
                  </button>{' '}
                  <button
                    type="button"
                    className="text-xs text-neutral-500 hover:text-red-400"
                    onClick={() => remove.mutate(row.id)}
                  >
                    apagar
                  </button>
                </td>
              </tr>
            ))}
            {overrides.length === 0 && !isLoading && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-neutral-500">
                  Nenhum override para {locale}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
